/**
 * YouTube Lightbox JavaScript
 * 
 * Automatically detects YouTube video links and opens them in a lightbox.
 * No data attributes needed - works on any link with a YouTube video URL.
 *
 * @package CanadaAbroad
 */

(function() {
    'use strict';

    // Lightbox instance
    var lightbox = null;
    var currentVideoId = null;
    var focusedElementBeforeOpen = null;

    /**
     * YouTube URL patterns for VIDEO detection
     * These patterns identify actual video URLs (not channels, playlists, etc.)
     */
    var videoPatterns = {
        // youtu.be/VIDEO_ID
        shortUrl: /^https?:\/\/(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]{11})/,
        // youtube.com/watch?v=VIDEO_ID
        watchUrl: /^https?:\/\/(?:www\.)?youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/,
        // youtube.com/embed/VIDEO_ID
        embedUrl: /^https?:\/\/(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
        // youtube.com/v/VIDEO_ID
        vUrl: /^https?:\/\/(?:www\.)?youtube\.com\/v\/([a-zA-Z0-9_-]{11})/,
        // youtube.com/shorts/VIDEO_ID
        shortsUrl: /^https?:\/\/(?:www\.)?youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/
    };

    /**
     * Non-video YouTube URL patterns (channels, playlists, etc.)
     * These should NOT open in lightbox
     */
    var nonVideoPatterns = [
        /^https?:\/\/(?:www\.)?youtube\.com\/channel\//,
        /^https?:\/\/(?:www\.)?youtube\.com\/c\//,
        /^https?:\/\/(?:www\.)?youtube\.com\/user\//,
        /^https?:\/\/(?:www\.)?youtube\.com\/@/,
        /^https?:\/\/(?:www\.)?youtube\.com\/playlist/,
        /^https?:\/\/(?:www\.)?youtube\.com\/feed\//,
        /^https?:\/\/(?:www\.)?youtube\.com\/gaming/,
        /^https?:\/\/(?:www\.)?youtube\.com\/premium/,
        /^https?:\/\/(?:www\.)?youtube\.com\/account/,
        /^https?:\/\/(?:www\.)?youtube\.com\/?$/ // YouTube homepage
    ];

    /**
     * Check if URL is a non-video YouTube URL
     * @param {string} url - URL to check
     * @returns {boolean}
     */
    function isNonVideoUrl(url) {
        for (var i = 0; i < nonVideoPatterns.length; i++) {
            if (nonVideoPatterns[i].test(url)) {
                return true;
            }
        }
        return false;
    }

    /**
     * Extract YouTube video ID from URL
     * @param {string} url - YouTube URL
     * @returns {string|null} Video ID or null if not a video URL
     */
    function getVideoId(url) {
        if (!url) return null;

        // First check if it's a non-video URL
        if (isNonVideoUrl(url)) {
            return null;
        }

        // Check each video pattern
        for (var patternName in videoPatterns) {
            var match = url.match(videoPatterns[patternName]);
            if (match && match[1]) {
                return match[1];
            }
        }

        return null;
    }

    /**
     * Create the lightbox HTML structure
     */
    function createLightbox() {
        if (lightbox) return lightbox;

        var overlay = document.createElement('div');
        overlay.className = 'youtube-lightbox-overlay';
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.setAttribute('aria-label', 'Video player');
        overlay.innerHTML = [
            '<div class="youtube-lightbox-container">',
            '  <button type="button" class="youtube-lightbox-close" aria-label="Close video">',
            '    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">',
            '      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>',
            '    </svg>',
            '  </button>',
            '  <div class="youtube-lightbox-video">',
            '    <div class="youtube-lightbox-loading">',
            '      <div class="youtube-lightbox-spinner"></div>',
            '      <span class="youtube-lightbox-loading-text">Loading video...</span>',
            '    </div>',
            '  </div>',
            '</div>'
        ].join('');

        document.body.appendChild(overlay);
        lightbox = overlay;

        // Event listener - ONLY close button closes the lightbox
        var closeButton = overlay.querySelector('.youtube-lightbox-close');
        closeButton.addEventListener('click', closeLightbox);

        return lightbox;
    }

    /**
     * Open the lightbox with a YouTube video
     * @param {string} videoId - YouTube video ID
     */
    function openLightbox(videoId) {
        if (!videoId) return;

        // Store currently focused element
        focusedElementBeforeOpen = document.activeElement;

        // Create lightbox if not exists
        createLightbox();

        currentVideoId = videoId;

        // Build embed URL
        var embedUrl = 'https://www.youtube.com/embed/' + videoId + '?autoplay=1&rel=0&modestbranding=1&playsinline=1';

        // Get video container
        var videoContainer = lightbox.querySelector('.youtube-lightbox-video');
        
        // Remove any existing iframe
        var existingIframe = videoContainer.querySelector('iframe');
        if (existingIframe) {
            existingIframe.remove();
        }

        // Reset loading state
        videoContainer.classList.remove('is-loaded');

        // Create iframe
        var iframe = document.createElement('iframe');
        iframe.setAttribute('src', embedUrl);
        iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute('title', 'YouTube video player');
        
        // Handle iframe load
        iframe.addEventListener('load', function() {
            videoContainer.classList.add('is-loaded');
        });

        videoContainer.appendChild(iframe);

        // Show lightbox
        document.body.classList.add('youtube-lightbox-open');
        lightbox.classList.add('is-active');
        lightbox.classList.remove('is-closing');

        // Focus close button for accessibility
        setTimeout(function() {
            var closeButton = lightbox.querySelector('.youtube-lightbox-close');
            if (closeButton) {
                closeButton.focus();
            }
        }, 100);

        // Dispatch custom event
        document.dispatchEvent(new CustomEvent('youtubeLightboxOpen', {
            detail: { videoId: videoId }
        }));
    }

    /**
     * Close the lightbox
     */
    function closeLightbox() {
        if (!lightbox || !lightbox.classList.contains('is-active')) return;

        lightbox.classList.add('is-closing');

        // Wait for animation
        setTimeout(function() {
            lightbox.classList.remove('is-active', 'is-closing');
            document.body.classList.remove('youtube-lightbox-open');

            // Remove iframe to stop video
            var iframe = lightbox.querySelector('iframe');
            if (iframe) {
                iframe.remove();
            }

            // Reset loading state
            var videoContainer = lightbox.querySelector('.youtube-lightbox-video');
            if (videoContainer) {
                videoContainer.classList.remove('is-loaded');
            }

            // Restore focus
            if (focusedElementBeforeOpen && focusedElementBeforeOpen.focus) {
                focusedElementBeforeOpen.focus();
            }

            // Dispatch custom event
            document.dispatchEvent(new CustomEvent('youtubeLightboxClose', {
                detail: { videoId: currentVideoId }
            }));

            currentVideoId = null;
        }, 300);
    }

    /**
     * Initialize - Listen for clicks on ALL links
     */
    function init() {
        // Use event delegation on document for all link clicks
        document.addEventListener('click', function(e) {
            // Find closest anchor element
            var link = e.target.closest('a[href]');
            
            if (!link) return;

            var href = link.getAttribute('href');
            if (!href) return;

            // Try to extract video ID
            var videoId = getVideoId(href);

            // If it's a YouTube video, open in lightbox
            if (videoId) {
                e.preventDefault();
                e.stopPropagation();
                openLightbox(videoId);
            }
            // Otherwise, let the link work normally (channels, playlists, non-YouTube links)
        });
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Expose public API
    window.YoutubeLightbox = {
        open: openLightbox,
        close: closeLightbox,
        getVideoId: getVideoId
    };

})();

